import { useEffect, useId, useRef, useState, useSyncExternalStore, type ReactNode, type SyntheticEvent } from 'react';
import { Icon, type IconName } from '../Icon';
import { motionMs } from './useContentMotion';
import { signOut, startSignIn, subscribeWebAuth, webAuthSnapshot } from '../../state/webAuth';
import { t } from '../../i18n/I18n';

export type DismissDialog = (after?: () => void) => void;
/**
 * The dialog whose action is handing over (`dismiss(after)` runs the action at once, without the exit). A dialog that
 * action opens takes over its veil rather than fading one in from nothing, which would show the page behind clear for
 * a moment between the two: «Создать свой интерьер» opening «Новый проект», «Начать с проекта по ссылке», «Удалить
 * навсегда».
 */
let handingOver: HTMLDialogElement | null = null;
/**
 * `leading` goes before the title in the header: a back button of a dialog with a second screen. `closeIcon` is the glyph
 * of its close button. `canDismiss` may keep the dialog open when Escape, the backdrop or the close button would close
 * it (a run that asks before it is stopped); `backdropDismiss` false leaves a click beside it to nothing (an alert).
 */
export function HomeDialog({ title, onClose, children, wide = false, className, leading, closeIcon = 'close', canDismiss, backdropDismiss = true, role }: { title: string; onClose: () => void; children: ReactNode | ((dismiss: DismissDialog) => ReactNode); wide?: boolean; className?: string; leading?: ReactNode; closeIcon?: IconName; canDismiss?: () => boolean; backdropDismiss?: boolean; role?: 'alertdialog' }) {
  const ref = useRef<HTMLDialogElement>(null);
  const animation = useRef<Animation | null>(null);
  const closing = useRef(false);
  const completion = useRef<(() => void) | null>(null);
  const onClosed = useRef(onClose); onClosed.current = onClose;
  const allowed = useRef(canDismiss); allowed.current = canDismiss;
  const [exiting, setExiting] = useState(false);
  const titleId = useId();
  const [previous] = useState(() => {
    const active = document.activeElement as HTMLElement | null;
    return active?.closest('details')?.querySelector('summary') ?? active;
  });
  // Opened by another dialog's action while that one is still up: the veil is already there.
  const [keepsVeil] = useState(() => !!handingOver?.isConnected && handingOver.matches(':modal'));
  const dismiss: DismissDialog = after => {
    if (closing.current) return;
    if (!after && allowed.current && !allowed.current()) return;
    closing.current = true;
    if (after) { handingOver = ref.current; onClosed.current(); after(); return; }
    if (ref.current) {
      const backdrop = getComputedStyle(ref.current, '::backdrop');
      ref.current.style.setProperty('--ws-exit-backdrop-color', backdrop.backgroundColor);
      ref.current.style.setProperty('--ws-exit-backdrop-blur', backdrop.backdropFilter);
    }
    completion.current = () => onClosed.current();
    setExiting(true);
  };
  useEffect(() => {
    const element = ref.current!;
    element.showModal();
    element.querySelector<HTMLElement>('input, select, textarea')?.focus();
    if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const style = getComputedStyle(element);
      animation.current = element.animate([{ opacity: 0, transform: 'translateY(12px) scale(.98)' }, { opacity: 1, transform: 'none' }], { duration: motionMs(style, '--ws-motion', 220), easing: style.getPropertyValue('--ws-ease').trim() });
    }
    return () => {
      if (handingOver === element) handingOver = null;
      completion.current = null;
      animation.current?.cancel();
      element.close();
      queueMicrotask(() => {
        if (document.querySelector('dialog:modal')) return;
        const target = previous?.isConnected ? previous : document.getElementById('workspace-content');
        target?.focus();
      });
    };
  }, [previous]);
  useEffect(() => {
    if (!exiting) return;
    const element = ref.current!;
    const preference = matchMedia('(prefers-reduced-motion: reduce)');
    let active = true;
    let fallback: ReturnType<typeof setTimeout> | undefined;
    const finish = () => {
      if (!active) return;
      active = false;
      const callback = completion.current;
      completion.current = null;
      callback?.();
    };
    const currentStyle = getComputedStyle(element);
    const from = { opacity: currentStyle.opacity, transform: currentStyle.transform };
    animation.current?.cancel();
    if (preference.matches || document.hidden) finish();
    else {
      const style = getComputedStyle(element);
      const duration = Math.min(250, Math.max(0, motionMs(style, '--ws-motion', 160) || 160));
      const exit = element.animate([from, { opacity: 0, transform: 'translateY(8px) scale(.985)' }], { duration, easing: style.getPropertyValue('--ws-ease').trim() || 'ease-out', fill: 'forwards' });
      animation.current = exit;
      void exit.finished.then(finish, finish);
      fallback = setTimeout(finish, duration + 60);
    }
    const changed = () => { if (preference.matches) { animation.current?.cancel(); finish(); } };
    const hidden = () => { if (document.hidden) { animation.current?.cancel(); finish(); } };
    preference.addEventListener('change', changed);
    document.addEventListener('visibilitychange', hidden);
    return () => { active = false; if (fallback !== undefined) clearTimeout(fallback); preference.removeEventListener('change', changed); document.removeEventListener('visibilitychange', hidden); animation.current?.cancel(); };
  }, [exiting]);
  const blockClosingInput = (event: SyntheticEvent) => {
    if (closing.current) { event.preventDefault(); event.stopPropagation(); }
  };
  return <dialog ref={ref} className={'workspace-dialog' + (wide ? ' wide' : '') + (className ? ' ' + className : '')} role={role} aria-labelledby={titleId} data-phase={exiting ? 'closing' : 'open'} data-veil={keepsVeil ? 'kept' : undefined}
    onClickCapture={blockClosingInput}
    onKeyDownCapture={blockClosingInput}
    // Keys pressed in a dialog are the dialog's: Delete or ⌘Z must not change the project behind it.
    onKeyDown={event => event.stopPropagation()}
    onSubmitCapture={blockClosingInput}
    onCancel={event => { event.preventDefault(); dismiss(); }} onClick={event => { if (backdropDismiss && event.target === event.currentTarget) { const rect = event.currentTarget.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dismiss(); } }}>
    <header>{leading}<h2 id={titleId}>{title}</h2><button className="ws-icon" aria-label={t('Close')} onClick={() => dismiss()}><Icon name={closeIcon} /></button></header>
    {typeof children === 'function' ? children(dismiss) : children}
  </dialog>;
}

/** Signing out forgets only the web's token: the site stays signed in, so the next «Войти» needs no form and no confirmation guards it. */
export function AccountDialog({ onClose }: { onClose: () => void }) {
  // Signing out keeps the closing dialog's face: it fades out as it was, not as the sign-in offer.
  const [signingOut, setSigningOut] = useState(false);
  const signedIn = useSyncExternalStore(subscribeWebAuth, webAuthSnapshot) !== null || signingOut;
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    // Back from the site restores this page from the back/forward cache with the press still pending.
    const restored = (event: PageTransitionEvent) => { if (event.persisted) setLeaving(false); };
    window.addEventListener('pageshow', restored);
    return () => window.removeEventListener('pageshow', restored);
  }, []);
  const signIn = () => {
    if (leaving) return;
    setLeaving(true);
    startSignIn(location.pathname + location.search);
  };
  return <HomeDialog title={t('#HomeAccountTitle')} onClose={onClose}>{dismiss => <>
    <div className="account-symbol"><Icon name="user" size={30} /></div>
    <h3>{t(signedIn ? '#HomeSignedIn' : '#HomeAccountHeadline')}</h3>
    <p>{t(signedIn ? '#HomeSignedInText' : '#HomeAccountText')}</p>
    <div className="account-note"><Icon name="folder" /><div><strong>{t('#HomeAccountProjectsTitle')}</strong><span>{t('#HomeAccountProjectsText')}</span></div></div>
    {signedIn ? <>
      <button className="ws-button primary account-signin" onClick={() => { setSigningOut(true); signOut(); dismiss(); }}>{t('Sign out')}</button>
      <button className="ws-button account-signin" onClick={() => dismiss()}>{t('Close')}</button>
    </> : <>
      <button className="ws-button primary account-signin" aria-busy={leaving || undefined} onClick={signIn}>{leaving && <span className="ws-spinner" aria-hidden="true" />}{t('#HomeSignInToAccount')}<Icon name="external" size={18} /></button>
      <p className="ws-caption">{t('#HomeSignInNote')}</p>
      <button className="ws-button account-signin" onClick={() => dismiss()}>{t('#HomeContinueAsGuest')}<Icon name="arrowRight" size={18} /></button>
    </>}
  </>}</HomeDialog>;
}
